import {
  type PaymentMethodId,
  type PaymentMethodLockup,
  type PaymentMethodTone,
  type PaymentMethodVariant
} from 'ecosystem'
import { type StaticImageData } from 'next/image'
import afterpayImg from './images/afterpay.svg'
import afterpayInverseImg from './images/afterpay-inverse.svg'
import alipayImg from './images/alipay.svg'
import almaImg from './images/alma.svg'
import amexImg from './images/amex.svg'
import apple_payImg from './images/apple-pay.svg'
import bancomat_payImg from './images/bancomat-pay.png'
import bancontactImg from './images/bancontact.svg'
import billieImg from './images/billie.svg'
import billieInverseImg from './images/billie-inverse.svg'
import blikImg from './images/blik.svg'
import cash_appImg from './images/cash-app.svg'
import clearpayImg from './images/clearpay.png'
import epsImg from './images/eps.svg'
import google_payImg from './images/google-pay.svg'
import google_payInverseImg from './images/google-pay-inverse.svg'
import idealImg from './images/ideal.svg'
import klarnaImg from './images/klarna.svg'
import mastercardImg from './images/mastercard.svg'
import mobilepayImg from './images/mobilepay.svg'
import multibancoImg from './images/multibanco.png'
import mybankImg from './images/mybank.svg'
import netsImg from './images/nets.svg'
import netsInverseImg from './images/nets-inverse.svg'
import nexiImg from './images/nexi.svg'
import nexiInverseImg from './images/nexi-inverse.svg'
import oneyImg from './images/oney.png'
import paypalDarkImg from './images/paypal-dark.svg'
import paypalInverseImg from './images/paypal-inverse.svg'
import paypalSymbolImg from './images/paypal-symbol.svg'
import paytrailImg from './images/paytrail.svg'
import przelewy24Img from './images/przelewy24.svg'
import qliroImg from './images/qliro.png'
import qliroInverseImg from './images/qliro-inverse.png'
import rivertyImg from './images/riverty.svg'
import satispayImg from './images/satispay.png'
import sepaImg from './images/sepa.svg'
import sveaImg from './images/svea.svg'
import sveaInverseImg from './images/svea-inverse.svg'
import swishImg from './images/swish.svg'
import swishInverseImg from './images/swish-inverse.svg'
import trustlyImg from './images/trustly.svg'
import trustlyInverseImg from './images/trustly-inverse.svg'
import twintImg from './images/twint.svg'
import vippsImg from './images/vipps.svg'
import visaImg from './images/visa.svg'
import visaInverseImg from './images/visa-inverse.svg'
import wechat_payImg from './images/wechat-pay.svg'
import zaverImg from './images/zaver.svg'
import zaverInverseImg from './images/zaver-inverse.svg'
import zaverOutlineImg from './images/zaver-outline.svg'
import zaverOutlineInverseImg from './images/zaver-outline-inverse.svg'
import zaverPlateImg from './images/zaver-plate.svg'

/**
 * The artwork available for one lockup, by tone. Sparse — most brands publish a single
 * treatment, and asking for a tone we do not hold falls back rather than failing.
 */
export type PaymentMethodToneSet = Partial<Record<PaymentMethodTone, StaticImageData>>

/**
 * Every lockup we ship for one method. Sparse for the same reason: the admin offers the
 * merchant exactly the combinations present here, so the catalogue can grow a variant
 * without any change to the picker.
 */
export type PaymentMethodAsset = Partial<Record<PaymentMethodLockup, PaymentMethodToneSet>>

/**
 * Bundled marks, keyed by {@link PaymentMethodId}.
 *
 * Sources: most are vectors from datatrans/payment-logos (CC BY-SA 4.0, modified — the
 * neutral white card backing was removed so the mark sits on transparent; coloured and
 * gradient tiles were kept because there the tile is part of the brand). The remainder
 * are official brand packs, or Briqpay's own method assets re-encoded from the 2560px
 * rasters they embed in an SVG wrapper down to a 96px-tall PNG.
 *
 * Every file is cropped to its ink, so a shared render height gives a shared visual
 * size. Without it the marks inherit each source's own padding convention — the
 * datatrans cards inset their artwork to about a third of the canvas height while the
 * brand packs run nearly edge to edge, which made PayPal a quarter the size of Bancomat
 * in the same row.
 *
 * Tone labels describe the *artwork*: `colour` has hue, `dark` is mono dark ink for
 * light backgrounds, `light` is the reverse. A brand whose only lockup is monochrome is
 * labelled honestly rather than as `colour`, which is what lets the admin warn that a
 * method will disappear on a dark surface.
 *
 * `Partial` on purpose: a selection referencing a method we have no asset for renders
 * nothing rather than a broken image, so the catalogue can grow without a migration.
 */
export const PAYMENT_METHOD_ASSETS: Partial<Record<PaymentMethodId, PaymentMethodAsset>> = {
  // Official Afterpay brand assets: the black and white horizontal wordmarks. The black
  // one carries no `fill` at all and relies on the SVG default — fine here, since these
  // render through `next/image` as an `<img>` and inherit nothing.
  afterpay: { standard: { dark: afterpayImg, light: afterpayInverseImg } },
  alipay: { standard: { colour: alipayImg } },
  alma: { standard: { colour: almaImg } },
  amex: { standard: { colour: amexImg } },
  // Apple's own Apple Pay Mark: white glyphs on the black rounded tile, where the tile
  // is the mark. Apple publishes no white lockup, so there is no `light` variant.
  'apple-pay': { standard: { dark: apple_payImg } },
  'bancomat-pay': { standard: { colour: bancomat_payImg } },
  bancontact: { standard: { colour: bancontactImg } },
  // Official Billie brand assets: near-black (#1F2530) and white.
  billie: { standard: { dark: billieImg, light: billieInverseImg } },
  blik: { standard: { dark: blikImg } },
  // Official Cash App Pay icon lockup, brand green. Served from Block's Afterpay CDN
  // (`static.afterpay.com/.../logo/icon/color.svg`) despite depicting Cash App — the two
  // products share one developer site, which also publishes mono black/white icons and
  // wider wordmark lockups that slot in beside this one.
  'cash-app': { icon: { colour: cash_appImg } },
  clearpay: { standard: { colour: clearpayImg } },
  eps: { standard: { colour: epsImg } },
  'google-pay': { standard: { colour: google_payImg, light: google_payInverseImg } },
  ideal: { standard: { colour: idealImg } },
  klarna: { standard: { colour: klarnaImg } },
  mastercard: { standard: { colour: mastercardImg } },
  mobilepay: { standard: { colour: mobilepayImg } },
  multibanco: { icon: { colour: multibancoImg } },
  mybank: { standard: { colour: mybankImg } },
  // Like Nexi, a gateway rather than a method — the two are one company now. Official
  // brand pack: teal/cyan and white, both drawn small inside a 283px square canvas, so
  // the crop to their shared 138x41 ink is what makes them sit level with the rest.
  nets: { standard: { colour: netsImg, light: netsInverseImg } },
  // Nexi is a gateway rather than a method a shopper picks, but merchants who run it
  // often want it shown. Official brand pack: blue (#2D32AA) and white. The two are
  // cropped to a shared frame rather than each to its own ink — Illustrator exported
  // the white one a pixel off, and per-file crops made the mark jump on a tone change.
  nexi: { standard: { colour: nexiImg, light: nexiInverseImg } },
  oney: { standard: { colour: oneyImg } },
  // Official PayPal brand pack, which is the whole catalogue PayPal publishes: a colour
  // symbol, and the wordmark in black or white. There is deliberately no coloured
  // wordmark — `standard` therefore has no `colour` tone, and `auto` resolves it to black
  // on light surfaces and white on dark ones, which is exactly PayPal's own guidance. The
  // two wordmarks share one frame (167x56), so a tone change cannot move the mark.
  paypal: {
    standard: { dark: paypalDarkImg, light: paypalInverseImg },
    icon: { colour: paypalSymbolImg }
  },
  paytrail: { icon: { colour: paytrailImg } },
  przelewy24: { standard: { colour: przelewy24Img } },
  // Qliro publishes two treatments of its mark, black and brand mint. Its `alt6_flat`
  // artwork is not a third lockup but a full payment-methods strip — several brands in
  // one image — so it lives in the strip catalogue, not here.
  qliro: { standard: { dark: qliroImg, light: qliroInverseImg } },
  riverty: { icon: { colour: rivertyImg } },
  satispay: { icon: { colour: satispayImg } },
  sepa: { standard: { colour: sepaImg } },
  // Official Svea brand assets: navy/cyan and white.
  svea: { standard: { colour: sveaImg, light: sveaInverseImg } },
  // Official Swish secondary logo (the horizontal mark + wordmark), both exports pinned
  // to one 577.62x175.83 frame — the brand ships them declaring 577.62x175.83 and 578x176
  // for identical ink. The gradient mark is common to both; the wordmark is near-black on
  // the light-background file and white on the dark one, which is the `light` tone.
  swish: { standard: { colour: swishImg, light: swishInverseImg } },
  // Official Trustly brand assets: black and white horizontal logotypes.
  trustly: { standard: { dark: trustlyImg, light: trustlyInverseImg } },
  twint: { standard: { colour: twintImg } },
  vipps: { standard: { colour: vippsImg } },
  visa: { standard: { colour: visaImg, light: visaInverseImg } },
  'wechat-pay': { standard: { colour: wechat_payImg } },
  // Official Zaver brand assets, grouped by how the mark is drawn rather than by tone
  // alone — the pack holds two different cuts of it, and mixing them across one lockup
  // would redraw the mark on a tone change.
  //
  // `standard` is the filled pink circle: coal wordmark for light surfaces, white for
  // dark ones, both keeping the brand pink, which is what Zaver leads with. `outline` is
  // the same mark as single-ink line art, in coal or white, for surfaces that want
  // restraint. Both pairs are internally frame-exact (566x150 filled, 566x149.98 lined),
  // so a tone change never moves the mark. `plate` puts the lockup on Zaver's own white
  // card — the one mark in the catalogue that brings its own background, for busy or
  // photographic surfaces where neither of the others holds up.
  zaver: {
    standard: { colour: zaverImg, light: zaverInverseImg },
    outline: { dark: zaverOutlineImg, light: zaverOutlineInverseImg },
    plate: { colour: zaverPlateImg }
  }
}

/** Preferred order when the merchant has expressed no lockup preference. */
// `outline` and `plate` last: line art is the restrained choice a merchant opts into, and
// the plate is the only lockup that paints its own background — neither should become a
// brand's default just by being the one we happen to hold.
const LOCKUP_PREFERENCE: PaymentMethodLockup[] = ['standard', 'wide', 'icon', 'outline', 'plate']

/** Lockups we actually hold for a method, in display order. Drives the admin picker. */
export const getPaymentMethodLockups = (id: PaymentMethodId): PaymentMethodLockup[] => {
  const asset = PAYMENT_METHOD_ASSETS[id]
  return asset ? LOCKUP_PREFERENCE.filter((lockup) => !!asset[lockup]) : []
}

/** Tones we hold for one lockup, in display order. Drives the admin picker. */
export const getPaymentMethodTones = (
  id: PaymentMethodId,
  lockup?: PaymentMethodLockup
): PaymentMethodTone[] => {
  const resolved = lockup ?? getPaymentMethodLockups(id)[0]
  const tones = resolved ? PAYMENT_METHOD_ASSETS[id]?.[resolved] : undefined
  return (['colour', 'dark', 'light'] as PaymentMethodTone[]).filter((tone) => !!tones?.[tone])
}

/**
 * Pick the file to render for one method.
 *
 * Both axes degrade instead of failing: an unavailable lockup falls back to the brand's
 * usual one, and an unavailable tone to the closest thing we hold. That keeps a stored
 * selection valid when the catalogue changes underneath it — a merchant who picked a
 * variant we later drop sees the brand's default rather than a gap in the row.
 */
export const resolvePaymentMethodMark = (
  id: PaymentMethodId,
  variant: PaymentMethodVariant | undefined,
  isDarkSurface: boolean
): StaticImageData | undefined => {
  const asset = PAYMENT_METHOD_ASSETS[id]

  if (!asset) {
    return undefined
  }

  const lockup =
    (variant?.lockup && asset[variant.lockup] ? variant.lockup : undefined) ??
    getPaymentMethodLockups(id)[0]
  const tones = lockup ? asset[lockup] : undefined

  if (!tones) {
    return undefined
  }

  const tone = variant?.tone ?? 'auto'
  const order: PaymentMethodTone[] =
    tone === 'auto'
      ? isDarkSurface
        ? ['light', 'colour', 'dark']
        : ['colour', 'dark', 'light']
      : [tone, 'colour', 'dark', 'light']

  return order.map((candidate) => tones[candidate]).find(Boolean)
}

/**
 * Display names for the catalogue.
 *
 * A total `Record` rather than a `Partial`: adding an id to {@link PaymentMethodId}
 * should fail the build until it has a name, because this is what the merchant picks
 * from in the admin — an unnamed id there is unpickable.
 */
export const PAYMENT_METHOD_LABELS: Record<PaymentMethodId, string> = {
  afterpay: 'Afterpay',
  alipay: 'Alipay',
  alma: 'Alma',
  amex: 'American Express',
  'apple-pay': 'Apple Pay',
  'bancomat-pay': 'BANCOMAT Pay',
  bancontact: 'Bancontact',
  billie: 'Billie',
  blik: 'BLIK',
  'cash-app': 'Cash App Pay',
  clearpay: 'Clearpay',
  eps: 'EPS',
  'google-pay': 'Google Pay',
  ideal: 'iDEAL',
  klarna: 'Klarna',
  mastercard: 'Mastercard',
  mobilepay: 'MobilePay',
  multibanco: 'Multibanco',
  mybank: 'MyBank',
  nets: 'Nets',
  nexi: 'Nexi',
  oney: 'Oney',
  paypal: 'PayPal',
  paytrail: 'Paytrail',
  przelewy24: 'Przelewy24',
  qliro: 'Qliro',
  riverty: 'Riverty',
  satispay: 'Satispay',
  sepa: 'SEPA Direct Debit',
  svea: 'Svea',
  swish: 'Swish',
  trustly: 'Trustly',
  twint: 'TWINT',
  vipps: 'Vipps',
  visa: 'Visa',
  'wechat-pay': 'WeChat Pay',
  zaver: 'Zaver'
}

/** Every catalogue id, alphabetical by display name — the admin's picker order. */
export const PAYMENT_METHOD_IDS = (Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethodId[]).sort(
  (a, b) => PAYMENT_METHOD_LABELS[a].localeCompare(PAYMENT_METHOD_LABELS[b])
)
